import { useState, useMemo, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Avatar } from "@/components/atoms/Avatar";
import { Badge } from "@/components/atoms/Badge";
import { CopyButton } from "@/components/atoms/CopyButton";
import { DiaryDateField } from "@/modules/diary/DiaryDateTimeField";
import { leadsService } from "@/services/leadsService";
import { branchesService } from "@/services/branchesService";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { SearchInput } from "@/components/molecules/SearchInput";
import { FilterBar, FilterBarBoundary, FilterBarLabel, FilterScope } from "@/components/molecules/FilterBar";
import { ColumnFilter, ColumnFilterField } from "@/components/molecules/ColumnFilter";
import { PageHeader } from "@/components/molecules/PageHeader";
import { ScopePicker } from "@/components/molecules/ScopePicker";
import { scopeParams } from "@/lib/scopeParams";
import { Pagination } from "@/components/molecules/Pagination";
import { RefreshButton } from "@/components/molecules/RefreshButton";
import { SavedFiltersButton } from "@/components/molecules/SavedFiltersButton";
import { Tooltip } from "@/components/atoms/Tooltip";
import { EmptyCell } from "./leadEmptyCells";
import { conversationEmptyReason, contactEmptyReason, propertyEmptyReason } from "./leadEmptyReasons";
import { DataTable, type Column } from "@/components/organisms/DataTable";
import { usePagedResource } from "@/hooks/usePagedResource";
import { usePageScope, useSettingsStore } from "@/store/settingsStore";
import { useUiStore } from "@/store/uiStore";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  showActionError,
  showActionErrorFromUnknown,
  showActionSuccess,
} from "@/lib/feedback";
import { getApiErrorMessage } from "@/utils/apiErrors";
import { cn } from "@/lib/cn";
import { formatDateAbsolute, formatEnquiryDateParts } from "@/utils/date-helpers";
import type { PageRequest, SortDirection } from "@/types/api";
import {
  Check,
  X,
  RotateCw,
  Loader2,
  PhoneIncoming,
  CheckCircle2,
  XCircle,
  CalendarCheck,
  Hourglass,
  BadgeCheck,
  Flame,
  TrendingUp,
  ExternalLink,
} from "lucide-react";
import type { LeadFilter, LeadSummary } from "@/types/entities";
import { ContactDetailDrawer } from "@/modules/contacts/ContactDetailDrawer";
import { LeadDetailDrawer } from "./LeadDetailDrawer";
import {
  CLEARED_LEAD_FILTER,
  LEAD_FILTER_PARAM_KEYS,
  parseLeadFilterFromSearchParams,
} from "./leadFilterParams";
import { useLeadFilterUrlSync } from "./useLeadFilterUrlSync";
import { useLeadPropertyNames } from "./useLeadPropertyNames";
import { initialPageWithUrlSort, sortDefaultsFromPage, useSortUrlSync } from "@/hooks/useSortUrlSync";

const WORKFLOW_STATE_ICON: Record<string, { icon: typeof PhoneIncoming; className: string }> = {
  "Picked up": { icon: PhoneIncoming, className: "text-blue-500" },
  "Dealt with": { icon: CheckCircle2, className: "text-slate-500" },
  Unqualified: { icon: XCircle, className: "text-red-500" },
  Booked: { icon: CalendarCheck, className: "text-violet-500" },
  "Waiting for reply": { icon: Hourglass, className: "text-amber-500" },
  Qualified: { icon: BadgeCheck, className: "text-emerald-500" },
  "Hot lead": { icon: Flame, className: "text-orange-500" },
  "Valuation opportunity": { icon: TrendingUp, className: "text-teal-500" },
};

function WorkflowStateDot({ state }: { state: string }) {
  const spec = WORKFLOW_STATE_ICON[state];
  if (!spec) return null;
  const Icon = spec.icon;
  return <Icon aria-hidden size={14} className={cn("shrink-0", spec.className)} />;
}

/** MenuSelect value sent to the API as `workflowState: null` when cleared. */
const WORKFLOW_STATE_NONE_VALUE = "__none__";

const LEADS_LIST_COLUMN_ORDER = [
  "enquirerName",
  "enquiryType",
  "enquiredProperty",
  "conversationStatus",
  "contactRefExternalId",
  "chatwootConversationLinks",
  "workflowState",
  "reachoutTriggered",
  "externalBranchId",
  "enquiryDate",
  "atlasModifiedAt",
  "atlasCreatedAt",
] as const;

const LEADS_LIST_COLUMN_RANK = new Map<string, number>(
  LEADS_LIST_COLUMN_ORDER.map((id, index) => [id, index]),
);

function workflowStateToSelectValue(state: string | null | undefined): string {
  const trimmed = state?.trim();
  return trimmed ? trimmed : WORKFLOW_STATE_NONE_VALUE;
}

function selectValueToWorkflowState(value: string): string | null {
  return value === WORKFLOW_STATE_NONE_VALUE ? null : value;
}

// Values must match the backend's LeadWorkflowState display labels exactly —
// the workflow-state endpoint takes that label, not the enum constant name.
const WORKFLOW_STATE_OPTIONS = [
  { value: WORKFLOW_STATE_NONE_VALUE, label: "None", triggerLabel: "—" },
  ...[
    { value: "Picked up", label: "Picked up" },
    { value: "Dealt with", label: "Dealt with" },
    { value: "Unqualified", label: "Unqualified" },
    { value: "Booked", label: "Booked" },
    { value: "Waiting for reply", label: "Waiting for reply" },
    { value: "Qualified", label: "Qualified" },
    { value: "Hot lead", label: "Hot lead" },
    { value: "Valuation opportunity", label: "Valuation opportunity" },
  ].map((opt) => ({ ...opt, leading: <WorkflowStateDot state={opt.value} /> })),
];

const WORKFLOW_STATE_FILTER_OPTIONS = [
  { value: "", label: "Any workflow state" },
  ...WORKFLOW_STATE_OPTIONS.filter((opt) => opt.value !== WORKFLOW_STATE_NONE_VALUE).map((opt) => ({
    value: opt.value,
    label: opt.label,
    leading: <WorkflowStateDot state={opt.value} />,
  })),
];

/** CRM `enquiryType` values (leads.enquiry_type). */
const ENQUIRY_TYPE_FILTER_OPTIONS = [
  { value: "", label: "Any intention" },
  { value: "LookingToBuy", label: "Looking to buy" },
  { value: "LookingToRent", label: "Looking to rent" },
  { value: "LookingToSell", label: "Looking to sell" },
  { value: "LookingToLet", label: "Looking to let" },
  { value: "Unspecified", label: "Unspecified" },
  { value: "Other", label: "Other" },
];

/** Pill colour for conversation_status values seen in data (extend when API documents enum). */
const CONVERSATION_STATUS_TONE: Record<
  string,
  "neutral" | "accent" | "success" | "warning" | "danger" | "info"
> = {
  UNQUALIFIED: "danger",
  IN_PROGRESS: "info",
  QUALIFIED: "success",
  COMPLETED: "success",
  BOOKED: "success",
};

function conversationStatusTone(status?: string | null) {
  if (!status) return "neutral";
  const key = status.trim().toUpperCase().replace(/[\s-]+/g, "_");
  return CONVERSATION_STATUS_TONE[key] ?? "neutral";
}

const LEADS_INITIAL_PAGE: PageRequest = { page: 0, size: 30, sort: "enquiryDate", direction: "desc" };
const LEADS_SORT_DEFAULTS = sortDefaultsFromPage(LEADS_INITIAL_PAGE);

function conversationStatusTooltip(row: LeadSummary): string {
  const status = row.conversationStatus?.trim();
  if (!status) return "";
  const updated = row.conversationStatusUpdatedAt?.trim();
  if (!updated) return status;
  const formatted = formatDateAbsolute(updated);
  return formatted ? `${status}\nUpdated ${formatted}` : status;
}

function EnquiryDateCell({ value }: { value: string | null | undefined }) {
  const parts = formatEnquiryDateParts(value);
  if (!parts) return <span className="text-xs text-bridge-subtle">—</span>;
  return (
    <div className="text-xs leading-tight tabular-nums text-slate-700">
      <div>{parts.date}</div>
      <div className="text-[11px] text-bridge-muted">{parts.time}</div>
    </div>
  );
}



export function LeadsPage() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  // Snapshot the landing URL so the first list fetch already carries those
  // filters — applying them in an effect would load the unfiltered page first.
  const [initialFilter] = useState(() => parseLeadFilterFromSearchParams(searchParams));

  const { legalEntityId } = useSettingsStore();
  const scope = usePageScope("leads");
  // The scope picker registers with the same FilterScope, so it counts towards the
  // badge and has to be reset by the same Clear filters control.
  const clearScope = useSettingsStore((s) => s.clearScope);
  const scopeQuery = useMemo(() => scopeParams(scope), [scope]);
  const { startPageLoading, stopPageLoading } = useUiStore();
  const [branchNameByExternalId, setBranchNameByExternalId] = useState<Map<string, string>>(
    () => new Map(),
  );

  const wrappedFetcher = useCallback(
    (f: LeadFilter, p: PageRequest) => {
      // The picker's selection wins where it has one; the org context is the fallback,
      // which is what an empty selection ("everything I can see") means.
      const filters = { ...f, legalEntityId: legalEntityId || undefined, ...scopeQuery };
      return leadsService.list(filters, p);
    },
    [legalEntityId, scopeQuery],
  );
  
  const [initialPage] = useState(() => initialPageWithUrlSort(searchParams, LEADS_INITIAL_PAGE));
  const list = usePagedResource<LeadSummary, LeadFilter>(wrappedFetcher, {
    initialPage,
    initialFilter,
  });
  const propertyNamesById = useLeadPropertyNames(list.data?.content ?? []);
  useSortUrlSync(list.pageRequest.sort, list.pageRequest.direction, list.setSort, LEADS_SORT_DEFAULTS);

  useEffect(() => {
    if (list.loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [list.loading, startPageLoading, stopPageLoading]);

  useEffect(() => {
    if (!list.error) return;
    toast.error(
      getApiErrorMessage(list.error) ?? "Failed to load leads. Please try again.",
      { toastId: "leads-list-error" },
    );
  }, [list.error]);

  useEffect(() => {
    if (!legalEntityId) {
      setBranchNameByExternalId(new Map());
      return;
    }

    let cancelled = false;
    branchesService
      .list({ legalEntityId }, { page: 0, size: 200 })
      .then((res) => {
        if (cancelled) return;
        const map = new Map<string, string>();
        for (const branch of res.content ?? []) {
          const name = branch.name?.trim();
          if (!name) continue;
          const externalId = branch.externalId != null ? String(branch.externalId).trim() : "";
          if (externalId) map.set(externalId, name);
          if (branch.id) map.set(branch.id, name);
        }
        setBranchNameByExternalId(map);
      })
      .catch(() => {
        if (!cancelled) setBranchNameByExternalId(new Map());
      });

    return () => {
      cancelled = true;
    };
  }, [legalEntityId]);

  const resolveBranchName = useCallback(
    (externalBranchId: string | number | null | undefined) => {
      const id = externalBranchId != null ? String(externalBranchId).trim() : "";
      if (!id) return "—";
      return branchNameByExternalId.get(id) ?? id;
    },
    [branchNameByExternalId],
  );
  /** Replace the branch UUID returned in reach-out errors with its display name. */
  const formatReachoutError = useCallback(
    (message: string) =>
      message.replace(
        /(\bon branch\s+)([0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12})\b/gi,
        (_match, prefix: string, branchId: string) => `${prefix}${resolveBranchName(branchId)}`,
      ),
    [resolveBranchName],
  );

  const [search, setSearch] = useState(initialFilter.search ?? "");
  const debouncedSearch = useDebouncedValue(search, 300);

  useEffect(() => {
    if ((list.filter.search ?? "") === debouncedSearch) return;
    list.setFilter({ search: debouncedSearch || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useLeadFilterUrlSync(list.filter, list.setFilter, setSearch);

  const sortObj = useMemo(
    () =>
      list.pageRequest.sort && list.pageRequest.direction
        ? { field: list.pageRequest.sort, direction: list.pageRequest.direction as SortDirection }
        : undefined,
    [list.pageRequest.sort, list.pageRequest.direction],
  );

  const handleRowClick = (lead: LeadSummary) => {
    setSelectedLeadId(lead.id);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedLeadId(null);
  };

  const [retriggeringId, setRetriggeringId] = useState<string | null>(null);

  const handleRetrigger = useCallback(async (e: React.MouseEvent, leadId: string) => {
    e.stopPropagation();
    setRetriggeringId(leadId);
    try {
      await leadsService.retrigger(leadId);
      // The retrigger endpoint only acknowledges the request, so the outcome has to be
      // read back off the lead itself before we can say whether the reachout went out.
      const lead = (await leadsService.getOne(leadId)) as LeadSummary | null;
      // The retrigger bumps `atlasModifiedAt`, so under the default ordering the row
      // moves to the top of the first page — reloading the current page would leave
      // the user staring at a window the lead has just left.
      list.reloadFirstPage();
      if (lead?.reachoutTriggered) {
        showActionSuccess(
          "Reach-out queued. The downstream flow will pick it up shortly.",
          { title: "Reach-out retriggered" },
        );
      } else {
        showActionError(
          formatReachoutError(lead?.reachoutError?.trim() || "Reachout retrigger failed."),
          { title: "Could not retrigger reach-out" },
        );
      }
    } catch (error) {
      console.error("Failed to retrigger lead", error);
      showActionErrorFromUnknown(error, "Failed to retrigger the lead. Please try again.", {
        title: "Could not retrigger reach-out",
      });
    } finally {
      setRetriggeringId(null);
    }
  }, [formatReachoutError, list]);

  const [workflowDraft, setWorkflowDraft] = useState<Record<string, string>>({});
  const [savingWorkflowId, setSavingWorkflowId] = useState<string | null>(null);

  const handleRefresh = useCallback(() => {
    // Unsaved (or failed) workflow-state picks otherwise keep overriding the
    // freshly fetched row forever, since the dropdown prefers the draft over
    // `r.workflowState` — refresh should discard them and show what the
    // server actually has.
    setWorkflowDraft({});
    list.reload();
  }, [list]);

  const handleWorkflowStateChange = useCallback((leadId: string, value: string) => {
    setWorkflowDraft((prev) => ({ ...prev, [leadId]: value }));
  }, []);

  const handleOpenCrmContact = useCallback((contactRefId: string) => {
    setSelectedContactId(contactRefId);
    setContactDrawerOpen(true);
  }, []);

  const handleCloseContactDrawer = useCallback(() => {
    setContactDrawerOpen(false);
    setSelectedContactId(null);
  }, []);

  const handleSaveWorkflowState = useCallback(async (e: React.MouseEvent, lead: LeadSummary) => {
    e.stopPropagation();
    const draftSelect = workflowDraft[lead.id];
    const savedSelect = workflowStateToSelectValue(lead.workflowState);
    if (draftSelect === undefined || draftSelect === savedSelect) return;
    const workflowState = selectValueToWorkflowState(draftSelect);
    setSavingWorkflowId(lead.id);
    try {
      await leadsService.updateWorkflowState(lead.id, workflowState);
      list.upsertEntry({ ...lead, workflowState: workflowState ?? undefined });
      setWorkflowDraft((prev) => {
        const next = { ...prev };
        delete next[lead.id];
        return next;
      });
      toast.success("Workflow state updated.", { toastId: "lead-workflow-state-updated" });
    } catch (error) {
      console.error("Failed to update lead workflow state", error);
      showActionErrorFromUnknown(error, "Failed to update the workflow state. Please try again.", {
        title: "Could not update lead",
      });
    } finally {
      setSavingWorkflowId(null);
    }
  }, [workflowDraft, list]);

  const columns = useMemo<Column<LeadSummary>[]>(() => {
    const columnDefinitions = [
    {
      id: "enquirerName",
      header: "Enquirer",
      sortable: true,
      width: "w-[310px]",
      cell: (r) => (
        <div className="flex min-w-0 items-center gap-2">
          <Avatar
            name={r.enquirerName || "(unnamed)"}
            size="sm"
            colorful
            className="shrink-0"
          />
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[13px] font-semibold text-slate-800" title={r.enquirerName ?? undefined}>{r.enquirerName ?? "(unnamed)"}</div>
            <div className="group/copy flex min-w-0 items-center gap-1">
              <div className="truncate text-[11px] text-bridge-muted" title={r.enquirerEmail ?? undefined}>{r.enquirerEmail ?? "no email"}</div>
              <CopyButton value={r.enquirerEmail} label="Email" />
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "enquirerMobilePhone",
      header: "Mobile Phone",
      width: "w-[140px]",
      className: "overflow-hidden",
      cell: (r) => {
        const phone = r.enquirerMobilePhone?.trim();
        if (!phone) return <span className="text-xs text-bridge-subtle">—</span>;
        return (
          <div className="group/copy flex min-w-0 items-center gap-1">
            <span className="block truncate text-xs tabular-nums text-slate-700">{phone}</span>
            <CopyButton value={r.enquirerMobilePhone} label="Phone number" />
          </div>
        );
      },
    },
    {
      id: "enquiryType",
      header: "Lead intention",
      sortable: true,
      width: "w-[160px]",
      className: "overflow-hidden",
      cellTooltip: (r) => r.enquiryType?.trim() ?? "",
      cell: (r) => {
        const type = r.enquiryType?.trim();
        if (!type) return <span className="text-xs text-bridge-subtle">—</span>;
        return <span className="block truncate text-xs text-slate-700">{type}</span>;
      },
      headerAccessory: (
        <ColumnFilter
          label="Lead intention"
          tooltip="Filter by enquiry type (e.g. LookingToRent)."
          width={240}
          active={Boolean(list.filter?.enquiryType)}
          onClear={() => list.setFilter({ enquiryType: undefined })}
        >
          <ColumnFilterField label="Intention">
            <MenuSelect
              hideLabel
              value={list.filter?.enquiryType ?? ""}
              onChange={(v) => list.setFilter({ enquiryType: v || undefined })}
              options={ENQUIRY_TYPE_FILTER_OPTIONS}
              minDropdownWidth={240}
            />
          </ColumnFilterField>
        </ColumnFilter>
      ),
    },
    {
      id: "enquiredProperty",
      header: "Property Enquired",
      width: "w-[240px]",
      className: "overflow-hidden",
      cellTooltip: (r) => {
        const propertyId = r.propertyId?.trim();
        if (!propertyId) return "";
        return propertyNamesById[propertyId]?.name ?? propertyId;
      },
      cell: (r) => {
        const propertyId = r.propertyId?.trim();
        if (!propertyId) return <EmptyCell reason={propertyEmptyReason()} />;
        const property = propertyNamesById[propertyId];
        if (!property || property.loading) {
          return <Loader2 size={14} className="animate-spin text-bridge-muted" aria-label="Loading property" />;
        }
        if (!property.name) {
          return (
            <span className="block truncate text-xs text-bridge-muted" title={propertyId}>
              Property unavailable
            </span>
          );
        }
        return <span className="block truncate text-xs text-slate-700">{property.name}</span>;
      },
    },
    {
      id: "workflowState",
      header: "Workflow State",
      width: "w-[280px]",
      headerAccessory: (
        <ColumnFilter
          label="Workflow State"
          tooltip="Show leads in a specific workflow state."
          width={260}
          active={Boolean(list.filter.workflowState)}
          onClear={() => list.setFilter({ workflowState: undefined })}
        >
          <ColumnFilterField label="State">
            <MenuSelect
              hideLabel
              value={list.filter.workflowState ?? ""}
              onChange={(v) => list.setFilter({ workflowState: v || undefined })}
              options={WORKFLOW_STATE_FILTER_OPTIONS}
              minDropdownWidth={260}
            />
          </ColumnFilterField>
        </ColumnFilter>
      ),
      cell: (r) => {
        const savedSelect = workflowStateToSelectValue(r.workflowState);
        const draftSelect = workflowDraft[r.id] ?? savedSelect;
        const isDirty = draftSelect !== savedSelect;
        const isSaving = savingWorkflowId === r.id;
        return (
          <div className="flex min-w-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <div className="min-w-0 flex-1">
              {/* This is a row edit control, not a filter — FilterBarBoundary keeps it
                  from registering into the surrounding FilterScope's active-filter tally. */}
              <FilterBarBoundary>
                <MenuSelect
                  hideLabel
                  value={draftSelect}
                  onChange={(v) => handleWorkflowStateChange(r.id, v)}
                  options={WORKFLOW_STATE_OPTIONS}
                  placeholder="—"
                  disabled={isSaving}
                  minDropdownWidth={260}
                  showSelectedTooltip
                />
              </FilterBarBoundary>
            </div>
            {isDirty ? (
              <Tooltip side="top" content="Save workflow state">
                <button
                  type="button"
                  aria-label="Save workflow state"
                  onClick={(e) => handleSaveWorkflowState(e, r)}
                  disabled={isSaving}
                  className={cn(
                    "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors",
                    "border-bridge-accent bg-bridge-accent text-white shadow-sm",
                    "hover:border-bridge-accentSoft hover:bg-bridge-accentSoft",
                    "disabled:cursor-not-allowed disabled:opacity-60",
                  )}
                >
                  {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={2.5} />}
                </button>
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "enquiryDate",
      header: "Enquiry Date",
      width: "w-[190px]",
      sortable: true,
      cell: (r) => <EnquiryDateCell value={r.enquiryDate} />,
      headerAccessory: (
        <ColumnFilter
          label="Enquiry Date"
          tooltip="Show leads whose enquiry date falls in this range."
          width={220}
          active={Boolean(list.filter.startDate || list.filter.endDate)}
          onClear={() => list.setFilter({ startDate: undefined, endDate: undefined })}
        >
          <ColumnFilterField label="From">
            <DiaryDateField
              value={list.filter.startDate ?? ""}
              max={list.filter.endDate}
              onChange={(v) => list.setFilter({ startDate: v || undefined })}
              placeholder="From date"
            />
          </ColumnFilterField>
          <ColumnFilterField label="To">
            <DiaryDateField
              value={list.filter.endDate ?? ""}
              min={list.filter.startDate}
              onChange={(v) => list.setFilter({ endDate: v || undefined })}
              placeholder="To date"
            />
          </ColumnFilterField>
        </ColumnFilter>
      ),
    },
    {
      id: "reachoutTriggered",
      header: "Reachout",
      align: "center",
      width: "w-[150px]",
      headerAccessory: (
      <ColumnFilter
        label="Reachout"
        tooltip="Show leads by whether an automated reachout was triggered, or by the error it failed with."
        width={230}
        active={list.filter.reachoutTriggered !== undefined || Boolean(list.filter.reachoutError)}
        onClear={() => list.setFilter({ reachoutTriggered: undefined, reachoutError: undefined })}
      >
        <ColumnFilterField label="Triggered">
          <MenuSelect
            hideLabel
            value={list.filter.reachoutTriggered === true ? "true" : list.filter.reachoutTriggered === false ? "false" : ""}
            onChange={(v) => list.setFilter({ reachoutTriggered: v === "true" ? true : v === "false" ? false : undefined })}
            options={[
              { value: "", label: "Any Reachout" },
              { value: "true", label: "Triggered" },
              { value: "false", label: "Not Triggered" },
            ]}
          />
        </ColumnFilterField>
        <ColumnFilterField label="Error message">
          <SearchInput
            value={list.filter.reachoutError || ""}
            onChange={(val) => list.setFilter({ reachoutError: val || undefined })}
            placeholder="Filter by error..."
          />
        </ColumnFilterField>
      </ColumnFilter>
      ),
      cell: (r) => (
        <div className="flex items-center gap-1.5">
          {r.reachoutTriggered ? <Check size={14} className="text-green-600" /> : <X size={14} className="text-red-600" />}
          <Tooltip side="top" hoverOnly content="Retrigger the lead">
            <button
              type="button"
              onClick={(e) => handleRetrigger(e, r.id)}
              className="text-bridge-muted hover:text-bridge-primary transition-colors flex items-center justify-center p-1 rounded-md hover:bg-slate-100"
              disabled={retriggeringId === r.id}
            >
              <RotateCw size={12} className={retriggeringId === r.id ? "animate-spin" : ""} />
            </button>
          </Tooltip>
        </div>
      ),
    },
    {
      id: "conversationStatus",
      header: "Conversation Status",
      align: "center",
      width: "w-[150px]",
      className: "overflow-hidden",
      cellTooltip: conversationStatusTooltip,
      cell: (r) => {
        const status = r.conversationStatus?.trim();
        if (!status) return <span className="text-xs text-bridge-subtle">—</span>;
        return (
          <Badge
            tone={conversationStatusTone(status)}
            className="h-5 max-w-full truncate rounded-full px-2 text-[10px] normal-case tracking-normal"
          >
            {status}
          </Badge>
        );
      },
    },
    {
      id: "chatwootConversationLinks",
      header: "Conversation Link",
      align: "center",
      width: "w-[240px]",
      cell: (r) => {
        const links = (r.chatwootConversationLinks ?? []).filter((l) => l.url?.trim());
        if (links.length === 0) return <EmptyCell reason={conversationEmptyReason(r)} onRetrigger={(e) => handleRetrigger(e, r.id)} retriggering={retriggeringId === r.id} />;
        // Conversation ids are scoped to a Chatwoot account, so different branches can
        // legitimately return the same number. Include the branch when several links are
        // present rather than making valid links look like duplicates.
        const single = links.length === 1;
        return (
          <div className="flex min-w-0 flex-col items-start gap-y-1">
            {links.map((link, i) => {
              const branch = link.branchId?.trim() ? resolveBranchName(link.branchId) : null;
              const conversationNumber = link.chatwootConversationId ?? i + 1;
              const branchLabel = branch && branch !== "—" ? branch : null;
              const accountLabel =
                link.chatwootAccountId !== undefined && link.chatwootAccountId !== null
                  ? `Account ${link.chatwootAccountId}`
                  : null;
              const contextLabel = branchLabel ?? accountLabel;
              const label = single
                ? "Open"
                : contextLabel
                  ? `${contextLabel} · #${conversationNumber}`
                  : `#${conversationNumber}`;
              const conversationSuffix = single ? "" : ` #${conversationNumber}`;
              return (
                <Tooltip
                  key={`${link.chatwootAccountId ?? "acc"}-${link.chatwootConversationId ?? i}-${i}`}
                  side="top"
                  hoverOnly
                  content={
                    branchLabel
                      ? `Open conversation${conversationSuffix} for ${branchLabel}`
                      : `Open conversation${conversationSuffix}`
                  }
                >
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex min-w-0 max-w-full items-center gap-1 text-xs font-medium text-bridge-accent hover:underline"
                  >
                    <span className="truncate">{label}</span>
                    <ExternalLink size={12} className="shrink-0" aria-hidden />
                  </a>
                </Tooltip>
              );
            })}
          </div>
        );
      },
    },
    {
      id: "externalBranchId",
      header: "Branch",
      sortable: true,
      align: "center",
      width: "max-w-[18rem] w-[18rem]",
      cell: (r) => {
        const name = resolveBranchName(r.externalBranchId);
        if (name === "—") return <span className="text-xs text-bridge-subtle">—</span>;
        return (
          <div className="flex flex-wrap justify-center gap-1" title={name}>
            <Badge tone="neutral" className="h-5 max-w-full truncate rounded-full px-2 text-[10px]">{name}</Badge>
          </div>
        );
      },
    },
    {
      id: "contactRefExternalId",
      header: "CRM Contact ID",
      align: "center",
      width: "w-[190px]",
      cell: (r) => (
        <div className="group/copy flex min-w-0 items-center justify-center gap-1">
          {r.contactRefExternalId && r.contactRefId ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (r.contactRefId) handleOpenCrmContact(r.contactRefId);
              }}
              className="truncate text-xs font-medium text-bridge-accent hover:underline"
              title="Open this contact"
            >
              {r.contactRefExternalId}
            </button>
          ) : (
            <span className="truncate text-xs text-slate-700">{r.contactRefExternalId ?? <EmptyCell reason={contactEmptyReason(r)} onRetrigger={(e) => handleRetrigger(e, r.id)} retriggering={retriggeringId === r.id} />}</span>
          )}
          {r.contactRefExternalId && <CopyButton value={r.contactRefExternalId} label="CRM Contact ID" />}
        </div>
      ),
      headerAccessory: (
        <ColumnFilter
          label="CRM Contact ID"
          tooltip="Show leads by whether they are linked to a CRM contact, or match one exact CRM contact ID."
          width={230}
          active={Boolean(list.filter.crmContactId)}
          onClear={() => list.setFilter({ crmContactId: undefined })}
        >
          <ColumnFilterField label="Linked">
            <MenuSelect
              hideLabel
              value={list.filter.crmContactId === "any" ? "any" : list.filter.crmContactId === "none" ? "none" : ""}
              onChange={(val) => list.setFilter({ crmContactId: val || undefined })}
              options={[
                { value: "", label: "Any CRM Contact" },
                { value: "any", label: "Has CRM Contact" },
                { value: "none", label: "No CRM Contact" },
              ]}
            />
          </ColumnFilterField>
          <ColumnFilterField label="Exact ID">
            <SearchInput
              value={
                list.filter.crmContactId !== "any" && list.filter.crmContactId !== "none"
                  ? list.filter.crmContactId || ""
                  : ""
              }
              onChange={(val) => list.setFilter({ crmContactId: val || undefined })}
              placeholder="Search CRM ID..."
            />
          </ColumnFilterField>
        </ColumnFilter>
      ),
    },
    {
      id: "atlasCreatedAt",
      header: "Created",
      align: "right",
      sortable: true,
      width: "w-[150px]",
      cell: (r) => <span className="text-xs text-bridge-muted">{formatDateAbsolute(r.atlasCreatedAt)}</span>,
    },
    {
      id: "atlasModifiedAt",
      header: "Updated",
      sortable: true,
      width: "w-[150px]",
      cell: (r) => <span className="text-xs text-bridge-muted">{formatDateAbsolute(r.atlasModifiedAt)}</span>,
      headerAccessory: (
        <ColumnFilter
          label="Updated"
          tooltip="Show leads last updated within this date range."
          width={220}
          active={Boolean(list.filter.updatedStartDate || list.filter.updatedEndDate)}
          onClear={() => list.setFilter({ updatedStartDate: undefined, updatedEndDate: undefined })}
        >
          <ColumnFilterField label="From">
            <DiaryDateField
              value={list.filter.updatedStartDate ?? ""}
              max={list.filter.updatedEndDate}
              onChange={(v) => list.setFilter({ updatedStartDate: v || undefined })}
              placeholder="From date"
            />
          </ColumnFilterField>
          <ColumnFilterField label="To">
            <DiaryDateField
              value={list.filter.updatedEndDate ?? ""}
              min={list.filter.updatedStartDate}
              onChange={(v) => list.setFilter({ updatedEndDate: v || undefined })}
              placeholder="To date"
            />
          </ColumnFilterField>
        </ColumnFilter>
      ),
    },
    ] satisfies Column<LeadSummary>[];

    return columnDefinitions
      .filter((column) => Boolean(column.id && LEADS_LIST_COLUMN_RANK.has(column.id)))
      .sort((left, right) => {
      const rankLeft = LEADS_LIST_COLUMN_RANK.get(left.id) ?? 0;
      const rankRight = LEADS_LIST_COLUMN_RANK.get(right.id) ?? 0;
      return rankLeft - rankRight;
      });
  }, [
    handleRetrigger,
    retriggeringId,
    list,
    resolveBranchName,
    handleOpenCrmContact,
    workflowDraft,
    savingWorkflowId,
    handleWorkflowStateChange,
    handleSaveWorkflowState,
    propertyNamesById,
  ]);

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Leads"
        actions={
          <>
            <SavedFiltersButton
              pageName="leads"
              filterParamKeys={LEAD_FILTER_PARAM_KEYS}
              scopePage="leads"
            />
            <RefreshButton onClick={handleRefresh} loading={list.loading} />
          </>
        }
      />
      {/* Spans the bar and the table so the column filters in the header count
          towards the same badge, and Clear Filters resets both at once. */}
      <FilterScope
        onClear={() => {
          setSearch("");
          clearScope("leads");
          // `clearFilter` would restore the landing-URL snapshot; dropping the
          // keys through setFilter both empties the list and lets the URL
          // effect strip the query params.
          list.setFilter(CLEARED_LEAD_FILTER);
        }}
      >
      <div className="flex-1 mx-6 mb-6 glass rounded-lg overflow-hidden flex flex-col">
        <FilterBar className="bg-bridge-background">
            <FilterBarLabel className="min-w-[80px]" />
            <ScopePicker
              page="leads"
              // A scope change can reduce the result count below the current
              // page. Start at the first page for the newly scoped results.
              onApply={() => list.setPage(0)}
            />
            <Tooltip side="top" hoverOnly content="Search leads by enquirer name, email or enquiry type.">
              <div>
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search enquirer, email, type..."
                />
              </div>
            </Tooltip>
            <Tooltip side="top" hoverOnly content="Show leads by their CRM sync state.">
              <div className="min-w-[160px] max-w-[200px]">
                <MenuSelect
                  value={list.filter.crmSyncStatus ?? ""}
                  onChange={(v) => list.setFilter({ crmSyncStatus: v || undefined })}
                  options={[
                    { value: "", label: "Any sync status" },
                    { value: "COMPLETED", label: "In Sync" },
                    { value: "IN_PROGRESS", label: "In Progress" },
                    { value: "FAILED", label: "Failed" },
                    { value: "CHECKPOINT", label: "Checkpoint" }
                  ]}
                  placeholder="Any sync status"
                />
              </div>
            </Tooltip>
            {/* Atlas contact has no column of its own, so it stays in the bar;
                the CRM contact, reachout and enquiry-date filters now live in
                their columns' headers. */}
            <Tooltip side="top" hoverOnly content="Show leads by whether they are linked to an Atlas contact.">
              <div className="min-w-[160px] max-w-[200px]">
                <MenuSelect
                  value={list.filter.atlasContactId === "any" ? "any" : list.filter.atlasContactId === "none" ? "none" : ""}
                  onChange={(val) => list.setFilter({ atlasContactId: val || undefined })}
                  options={[
                    { value: "", label: "Any Atlas Contact" },
                    { value: "any", label: "Has Atlas Contact" },
                    { value: "none", label: "No Atlas Contact" },
                  ]}
                />
              </div>
            </Tooltip>
            <Tooltip side="top" hoverOnly content="Show only the lead matching this exact Atlas contact ID.">
              <div className="w-[200px]">
                <SearchInput
                  value={(list.filter.atlasContactId !== "any" && list.filter.atlasContactId !== "none") ? (list.filter.atlasContactId || "") : ""}
                  onChange={(val) => list.setFilter({ atlasContactId: val || undefined })}
                  placeholder="Search Atlas ID..."
                />
              </div>
            </Tooltip>
        </FilterBar>

        <div className="flex-1 overflow-auto">
          <DataTable
            rows={list.data?.content ?? []}
            columns={columns}
            loading={list.loading}
            dense
            rowKey={(r) => r.id}
            onRowClick={handleRowClick}
            sort={sortObj}
            onSortChange={list.setSort}
            customization={{
              tableId: "leads",
              lockedColumnIds: ["enquirerName", "atlasCreatedAt"],
              optionalColumnIds: ["atlasModifiedAt"],
              pinnedStartColumnIds: ["enquirerName"],
            }}
            emptyTitle="No leads match"
          />
        </div>
        <Pagination
          page={list.data?.page ?? 0}
          totalPages={list.data?.totalPages ?? 0}
          totalElements={list.data?.totalElements ?? 0}
          size={list.data?.size ?? 20}
          onPageChange={list.setPage}
        />
      </div>
      </FilterScope>

      <LeadDetailDrawer
        open={drawerOpen} 
        leadId={selectedLeadId} 
        onClose={handleCloseDrawer} 
      />

      <ContactDetailDrawer
        open={contactDrawerOpen}
        contactId={selectedContactId}
        onClose={handleCloseContactDrawer}
        onUpdate={() => {}}
      />
    </div>
  );
}
